import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { gameReducer, initialState } from '../reducers/gameReducer';
import { loadGameState, saveGameState } from '../utils/storage';
import { checkAchievements } from '../utils/achievements';
import { LOGIN_REWARDS } from '../constants/rewards';

const GameContext = createContext();

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};

export const GameProvider = ({ children }) => {
  const [state, dispatch] = useReducer(gameReducer, initialState);

  // 載入遊戲狀態
  useEffect(() => {
    const loadState = async () => {
      try {
        const savedState = await loadGameState();
        if (savedState) {
          dispatch({ type: 'LOAD_STATE', payload: savedState });
        }
        // 🔥 載入後檢查每日登入
        dispatch({ type: 'CHECK_DAILY_LOGIN' });
      } catch (error) {
        console.error('載入遊戲狀態失敗:', error);
        dispatch({ type: 'CHECK_DAILY_LOGIN' });
      }
    };
    loadState();
  }, []);

  // 自動儲存 (每30秒)
  useEffect(() => {
    const saveInterval = setInterval(() => {
      saveGameState(state);
    }, 30000);
    return () => clearInterval(saveInterval);
  }, [state]);

  // 🔥 頁面關閉前儲存
  useEffect(() => {
    const handleBeforeUnload = () => {
      saveGameState(state);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [state]);

  // 自動敲擊
  useEffect(() => {
    if (!state.autoTapActive) return;
    const autoTapInterval = setInterval(() => {
      dispatch({ type: 'TAP_MOKUGYO' });
      dispatch({ type: 'AUTO_TAP_TICK' });
    }, 1000);
    return () => clearInterval(autoTapInterval);
  }, [state.autoTapActive]);

  // 檢查成就
  useEffect(() => {
    checkAchievements(state, dispatch);
  }, [
    state.totalTaps,
    state.combo,
    state.maxCombo,
    state.fastestTapInterval,
    state.autoTapTotal,
    state.nightTaps,
    state.morningTaps,
    state.perfectCombo,
    state.sessionTaps,
    state.lastTapIntervals,
    state.comboRecoveries,
  ]);

  // 🔥 每日登入獎勵領取
  const claimDailyReward = () => {
    if (state.dailyRewardClaimed) return;
    const dayIndex = (state.loginDays - 1) % LOGIN_REWARDS.length;
    const reward = LOGIN_REWARDS[dayIndex];
    dispatch({ type: 'CLAIM_DAILY_REWARD', payload: reward });
  };

  const actions = {
    tapMokugyo: () => dispatch({ type: 'TAP_MOKUGYO' }),
    changeType: (type) => dispatch({ type: 'CHANGE_TYPE', payload: type }),
    toggleAutoTap: () => dispatch({ type: 'TOGGLE_AUTO_TAP' }),
    selectSpecialFish: (fishId) => dispatch({ type: 'SELECT_SPECIAL_FISH', payload: fishId }),
    unlockAchievement: (achievement) => dispatch({ type: 'UNLOCK_ACHIEVEMENT', payload: achievement }),
    claimDailyReward,
  };

  return (
    <GameContext.Provider value={{ state, dispatch, ...actions }}>
      {children}
    </GameContext.Provider>
  );
};